import React, { useCallback, useEffect, useRef, useState } from "react";

import { TimeZone } from "~/shared.types";
import { useSchedulingTimezoneStore } from "~/stores";
import { icons } from "~/ui";
import { tw } from "~/utils";
import { TIME_ZONE_LIST } from "../utils";

const DROPDOWN_MIN_WIDTH = 288; // matches md:min-w-72

type TimeZoneSelectProps = {
  selectedTimeZone: TimeZone;
  onTimeZoneChange: (timeZone: TimeZone) => void;
  className?: string;
};

export const TimeZoneSelect = ({
  selectedTimeZone,
  onTimeZoneChange,
  className,
}: TimeZoneSelectProps) => {
  const { isTimezoneSelectOpen: isOpen, setIsTimezoneSelectOpen: setIsOpen } =
    useSchedulingTimezoneStore();
  const [openLeft, setOpenLeft] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const calculateDirection = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    // width is 0 when the button is CSS-hidden; skip in that case
    if (rect.width === 0) return;
    setOpenLeft(rect.right >= DROPDOWN_MIN_WIDTH);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Skip if this instance is CSS-hidden (e.g. the LeftPanel copy on mobile)
      if (
        !buttonRef.current ||
        buttonRef.current.getBoundingClientRect().width === 0
      )
        return;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  useEffect(() => {
    const observer = new ResizeObserver(calculateDirection);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [calculateDirection]);

  // Recalculate direction when this instance becomes the visible one
  useEffect(() => {
    if (isOpen) calculateDirection();
  }, [isOpen, calculateDirection]);

  // Reset open state on unmount so stale state doesn't persist on re-entry
  useEffect(() => () => setIsOpen(false), [setIsOpen]);

  const handleToggle = () => {
    calculateDirection();
    setIsOpen(!isOpen);
  };

  const handleSelect = (timeZone: TimeZone) => {
    onTimeZoneChange(timeZone);
    setIsOpen(false);
  };

  return (
    <div className={tw("relative", className)} ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        aria-label={`Timezone, ${selectedTimeZone.code}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        className="flex w-full cursor-pointer items-center justify-between gap-2 rounded bg-transparent p-1.5 hover:bg-gray-50 focus:ring-0"
      >
        <div className="flex items-center gap-1">
          <span className="">
            <icons.outline.GlobeAmericasIcon className="text-indigo flex size-5" />
          </span>
          <span className="text-indigo text-sm font-medium">
            {selectedTimeZone.code}
          </span>
        </div>
        <icons.outline.ChevronDownIcon
          className={tw(
            "text-indigo size-5 shrink-0 stroke-2 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <div
          className={tw(
            "border-blueGrey z-15 min-w-68 absolute top-full mt-1 max-h-80 overflow-y-auto rounded border bg-white shadow-lg md:min-w-72",
            openLeft ? "right-0" : "left-0",
          )}
        >
          {TIME_ZONE_LIST.map((timeZone) => (
            <button
              key={timeZone.code}
              aria-current={selectedTimeZone.code === timeZone.code}
              onClick={() => handleSelect(timeZone)}
              className={tw(
                "text-blueGrey w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-gray-50",
                selectedTimeZone.code === timeZone.code &&
                  "bg-light-lavender-gray hover:light-lavender-gray/80 text-white",
              )}
            >
              <div className="text-indigo flex items-center gap-1 text-xs font-medium md:text-sm">
                <span>{timeZone.code}</span>
                <div className="h-3 border-l"></div>
                <span>{timeZone.name}</span>
                <span className="font-light">({timeZone.city})</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
